import { MinusCircleTwoTone } from "@ant-design/icons";
import { Form, Input, Select, Col, Row, Button, InputNumber } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import FieldWithInfo from "@/components/Elements/FieldWithInfo";
import { COLORS } from "@/constants/Global";
import { useReferentialProjectCostTypes } from "@/entities/referential/referential-project-cost-types";
import { useFormRules } from "@/hooks/useFormRules";
import { getFieldErrorProps } from "@/modules/form-validation";
import { convertItemWithSlugToOption } from "@/modules/select";
import { TranslateCostSymbol } from "@/modules/translate";

import { useAdditionalCostTypeDescription } from "./useAdditionalCostTypeDescription";

const AdditionalCostsFormRow = ({
  name,
  errors,
  initialType,
  onRemove,
  ...restField
}) => {
  const { t } = useTranslation();
  const { REQUIRED } = useFormRules();

  const { typeDescription, onTypeChange } =
    useAdditionalCostTypeDescription(initialType);
  const { costTypes, isLoading } = useReferentialProjectCostTypes();

  if (isLoading) {
    return null;
  }

  return (
    <Row
      className="additional-costs-row"
      key={restField.fieldKey}
      align="stretch"
    >
      <Col span={5}>
        <FieldWithInfo
          info={typeDescription}
          field={
            <Form.Item
              {...restField}
              {...getFieldErrorProps(errors?.type)}
              name={[name, "type"]}
              rules={[REQUIRED]}
            >
              <Select
                options={costTypes.map(convertItemWithSlugToOption)}
                onChange={onTypeChange}
                style={{ flexGrow: 1 }}
                popupMatchSelectWidth={false}
              />
            </Form.Item>
          }
        />
      </Col>
      <Col span={6}>
        <Form.Item
          {...restField}
          {...getFieldErrorProps(errors?.description)}
          name={[name, "description"]}
        >
          <Input
            placeholder={t("project:additionalCosts.description")}
            data-testid="description"
          />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item
          {...restField}
          {...getFieldErrorProps(errors?.precision)}
          name={[name, "precision"]}
          rules={[REQUIRED]}
        >
          <InputNumber
            min={0}
            max={100}
            suffix="%"
            prefix="+/-"
            placeholder={t("project:additionalCosts.precision")}
            data-testid="precision"
          />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item
          {...restField}
          {...getFieldErrorProps(errors?.source)}
          name={[name, "source"]}
        >
          <Input
            placeholder={t("project:additionalCosts.source")}
            data-testid="source"
          />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item
          {...restField}
          {...getFieldErrorProps(errors?.estimated_cost)}
          name={[name, "estimated_cost"]}
          rules={[REQUIRED]}
        >
          <InputNumber
            min={0}
            prefix={<TranslateCostSymbol />}
            data-testid="estimated_cost"
            placeholder={t("project:additionalCosts.estimatedCost")}
          />
        </Form.Item>
      </Col>
      <Col>
        <Button
          icon={<MinusCircleTwoTone twoToneColor={COLORS.RED_TWO_TONE_COLOR} />}
          type="link"
          onClick={onRemove}
          size="small"
          style={{ marginTop: "5px", padding: "0" }}
        />
      </Col>
    </Row>
  );
};

AdditionalCostsFormRow.propTypes = {
  name: PropTypes.any.isRequired,
  initialType: PropTypes.string,
  errors: PropTypes.object,
  onRemove: PropTypes.func.isRequired,
};

export default AdditionalCostsFormRow;
